import { getApiUrl } from "@/lib/config";

/** Server-side import straight from other systems (api/documents/imports/remote). */
export type RemoteProviderId = "docusign" | "clicksign" | "adobesign" | "gdrive" | "onedrive";

export interface RemoteProvider {
  id: RemoteProviderId;
  label: string;
  kind: "esign" | "drive";
  /** FillSign has an app registered at the provider (sign-in button). */
  oauth: boolean;
  /** Accepts a pasted API token (Clicksign, Acrobat Sign). */
  token: boolean;
}

export interface RemoteConnection {
  id: string;
  provider: RemoteProviderId;
  providerLabel: string;
  authType: "oauth" | "token";
  account?: string | null;
  status: "active" | "reconnect";
  lastError?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type RemoteJobStatus = "queued" | "running" | "waiting" | "paused" | "reconnect" | "completed" | "failed" | "cancelled";

export interface RemoteJob {
  id: string;
  importId: string;
  provider: RemoteProviderId;
  providerLabel: string;
  account?: string | null;
  source?: string | null;
  fromDate?: string | null;
  status: RemoteJobStatus;
  nextRunAt?: string | null;
  lastError?: string | null;
  listed: number;
  skipped: number;
  expected: number;
  imported: number;
  duplicates: number;
  failed: number;
  importName?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
}

export interface RemoteOverview {
  enabled: boolean;
  providers: RemoteProvider[];
  connections: RemoteConnection[];
  jobs: RemoteJob[];
  lastTick?: string | null;
  /** The server tick ran in the last 3 minutes; when false the open page moves the jobs itself. */
  tickHealthy: boolean;
}

export class RemoteError extends Error {
  constructor(public status: number, message: string, public code?: string) { super(message); }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(getApiUrl(path), {
    method,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let code: string | undefined;
    try { const j = await res.json(); message = j?.message || message; code = j?.code; } catch { /* not JSON */ }
    throw new RemoteError(res.status, message, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const remoteApi = {
  overview: () => call<RemoteOverview>("GET", "/api/documents/imports/remote"),
  connectUrl: (provider: RemoteProviderId) => call<{ url: string }>("GET", `/api/documents/imports/remote/connect/${provider}`),
  connectToken: (provider: RemoteProviderId, token: string, sandbox: boolean) =>
    call<RemoteConnection>("POST", "/api/documents/imports/remote/connections/token", { provider, token, sandbox }),
  disconnect: (id: string) => call<void>("DELETE", `/api/documents/imports/remote/connections/${id}`),
  createJob: (body: { connectionId: string; name?: string; folder?: string; fromDate?: string }) =>
    call<RemoteJob>("POST", "/api/documents/imports/remote/jobs", body),
  control: (id: string, op: "pause" | "resume" | "cancel") => call<{ id: string; status: RemoteJobStatus }>("POST", `/api/documents/imports/remote/jobs/${id}/${op}`),
  run: () => call<unknown>("POST", "/api/documents/imports/remote/run"),
};

export const ACTIVE_JOB: RemoteJobStatus[] = ["queued", "running", "waiting"];
